import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Form, message, Divider, Spin } from "antd";
import { OrderedMap, Map } from "immutable";
import {
    fieldGenerator,
    getErrorOnField,
    getValidateStatus,
    submitForm,
    getValidateMessage
} from "utilities/form";
import CompanyService from "services/company-service";
import CaseService from "services/case-service";
import { GSTINDataRenderer } from "components";
import { INPUT_TYPES, 
    COMPANY_TYPES,
    // RESIDENCE_TYPES 
    GENDER_TYPES,
    NATURE_OF_ASSOCIATION_TYPES
} from "constants/index";
import { 
    // CASE_LEAD_TYPES,
    // CASE_PRIORITY_TYPES,
    CASE_CATEGORY_TYPES,
    PROGRAM_TYPE_NEW,
    BUSINESS_PRODUCT_TYPE_UPDATED,
    PRIMARY_LANGUAGE,
    SECONDARY_LANGUAGE,
    // ADDRESSES_TYPES
} from "constants/enums";
import { GSTIN_REGEX } from "constants/regex";
import moment from "moment";
import { List } from "immutable";
import _startCase from "lodash/startCase";
import _lower from "lodash/toLower";
import UnderwritingService from "services/underwriting-service";

const CASE_CATEGORY_OPTIONS = CASE_CATEGORY_TYPES.map((value, label) => ({ label, value})).toList();
const BUSINESS_PRODUCT_TYPE_OPTIONS= BUSINESS_PRODUCT_TYPE_UPDATED.map((value, label) => ({ label, value})).toList();
const PROGRAM_TYPE_OPTIONS = PROGRAM_TYPE_NEW.map((value, label) => ({ label, value})).toList();
const ALLOWED_STAGE_IDS = [4, 5, 7, 8, 18, 20];
const ALLOWED_LENDERS = [
  "HHPL",
  "ABFL|HHPL",
  "MFL|HHPL",
  "MFL",
  "KSF|HHPL"
];
const FORM_FIELDS = (
    GSTINvalidatorRule,
    LedgerMasterValidatorRule,
    ledgerMasterSearchByLedgerName,
    formatData,
    showDescriptionBPT,
    stageId,
    form
) =>
    new OrderedMap({
        type: {
            className: "form-field fb-33",
            label: "Type",
            type: INPUT_TYPES.SELECT,
            options: CASE_CATEGORY_OPTIONS,
            // defaultValue: CASE_CATEGORY_OPTIONS.first().value
        },
        business_product_type: {
            className: "form-field fb-33",
            label: "Product Type",
            type: INPUT_TYPES.SELECT,
            options: BUSINESS_PRODUCT_TYPE_OPTIONS,
            onChange: (value) => showDescriptionBPT(value),
            disabled: true
        },
        program_type: {
            className: "form-field fb-33",
            label: "Program Type",
            type: INPUT_TYPES.SELECT,
            options: PROGRAM_TYPE_OPTIONS,
            disabled: true
        },
        gstin: {
            className: "form-field fb-33",
            label: "GSTIN",
            type: INPUT_TYPES.TEXT,
            placeholder: "29ABCDE1234F2Z2",
            hasFeedback: true,
            rules: [GSTINvalidatorRule, {
                required: true,
                message: "GSTIN is Required"
            }]
        },
        // pan: {
        //     className: "form-field fb-50",
        //     label: "PAN",
        //     type: INPUT_TYPES.TEXT,
        //     placeholder: "29ABCDE1234F2Z2",
        //     hasFeedback: true,
        //     // rules: [GSTINvalidatorRule]
        // },
        company_name: {
            className: "form-field fb-33",
            label: "Company Legal Name",
            type: INPUT_TYPES.TEXT,
            rules: [
                {
                    required: true,
                    message: "Company Legal Name is Required"
                },
            ]

        },
        previous_case_via_existing_limit: {
            className: "form-field fb-33",
            label: "Previous Case Details",
            type: INPUT_TYPES.TEXT,
            placeholder:"Select a Case",
        },
        // anchor: {
        //     className: "form-field fb-33",
        //     label: "Anchor",
        //     type: INPUT_TYPES.OBJECT_SELECT,
        //     showSearch: true,
        //     labelProp: "name",
        //     onSearch: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.Anchor, str),
        //     rules: [{
        //         required: true,
        //         message: "Field Required"
        //     }]
        // },
        sellers: {
            className: "form-field fb-33",
            label: "Sellers",
            type: INPUT_TYPES.OBJECT_SELECT,
            mode: "multiple",
            showSearch: true,
            labelProp: "name",
            onSearch: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Distributor"), str),
            formatData,
            rules: [{
                required: true,
                message: "Field is Required"
            }]
        },
        sales_manager_user_id: {
            className: "form-field fb-33",
            label: "Area Sales Manager",
            type: INPUT_TYPES.SELECT,
            showSearch: true,
            rules: [
                {
                    required: true,
                    message: "Field is Required"
                }
            ],
        },
        sales_manager_users: {
            className: "form-field fb-33",
            label: "Secondary Sales Manager Users",
            type: INPUT_TYPES.SELECT,
            mode: "multiple",
        },
        inside_sales_personnel: {
            className: "form-field fb-30 mr-3perc",
            label: "Inside Sales Personnel",
            type: INPUT_TYPES.OBJECT_SELECT,
            notFoundContent: "This Sales person doesn't exist, you need to create it.",
            showSearch: true,
            rules: [
                {
                    required: false,
                    message: "Field Required"
                }
            ],
            labelProp: "full_name",
            onSearch: () => CompanyService.getISPList(),
            getOptionsOnFocus: () => CompanyService.getISPList()
        },
        
        bank: {
            className: "form-field fb-33",
            label: "Lending Partner",
            type: INPUT_TYPES.OBJECT_SELECT,
            showSearch: true,
            // getOptionsOnFocus: CompanyService.getLendingPartners,
            getOptionsOnFocus: () =>
                CompanyService.getLendingPartners().then((res) => {
                    let selectedBank = form?.getFieldValue("bank");
                    if (ALLOWED_STAGE_IDS.includes(stageId)) {
                        let options = res?.data.filter(el=>ALLOWED_LENDERS.includes(el.code));
                        if (
                            selectedBank &&
                            !ALLOWED_LENDERS.includes(selectedBank.code)
                          ) {
                            selectedBank = {...selectedBank, disabled: true}
                            options = [
                              ...options,
                              selectedBank
                            ];
                          }
                        return {data: options};
                    }
                return res;
                }),
            labelProp: "name"
        },
        primary_language: {
            className: "form-field fb-33",
            label: "Primary Language",
            type: INPUT_TYPES.SELECT,
            rules: [
                {
                    required: true,
                    message: "Field is Required"
                }
            ],
            options: PRIMARY_LANGUAGE.map((value, label) => ({
                label, value
            })).toList(),
        },
        secondary_language: {
            className: "form-field fb-33",
            label: "Secondary Language",
            type: INPUT_TYPES.SELECT,
            options: SECONDARY_LANGUAGE.map((value, label) => ({
                label, value
            })).toList(),
        },
        // priority: {
        //     className: "form-field fb-33",
        //     label: "Priority",
        //     type: INPUT_TYPES.SELECT,
        //     options: CASE_PRIORITY_TYPES.map((_,k) => ({
        //         label: k,
        //         value: _
        //     })).toList(),
        // },
        // lead_type: {
        //     className: "form-field fb-33",
        //     label: "Lead Type",
        //     type: INPUT_TYPES.SELECT,
        //     options: CASE_LEAD_TYPES.map((_,k) => ({
        //         label: k,
        //         value: _
        //     })).toList(),
        // },
        ledger_masters: {
            className: "form-field fb-100",
            label: "Supply Chain",
            type: INPUT_TYPES.MULTI_TABLE_SELECT,
            labelProp: "seller_name",
            placholder: "Attach SupplyChain data by searching ledger name",
            showSearch: true,
            onSearch: ledgerMasterSearchByLedgerName,
            labelFunc: _ => `${_.name} | ${(_.seller && _.seller.name) || " "} | ${_.create_date && moment(_.create_date).format("DD MMM YY")}`,
            valueFunc: _ => String(_.id),
            keyFunc: _ => String(_.id),
            rules: [LedgerMasterValidatorRule]
        },
        product: {
            className: "form-field fb-50",
            label: "Product",
            type: INPUT_TYPES.OBJECT_SELECT,
            getOptionsOnFocus: CompanyService.getProducts,
            labelProp: "name",
            rules: [
                {
                    required: true,
                    message: "Field is Required"
                }
            ]
        },
        proposed_limit: {
            className: "form-field fb-50",
            label: "Proposed Limit",
            type: INPUT_TYPES.TEXT,
            disabled: true,
            required: true
        },
        
    });

const getUserDetails = (allFieldsDisabled) => new OrderedMap({
    first_name: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "First Name",
        disabled: allFieldsDisabled,
        rules: [
            {
                required: true,
                message: "First Name is Required"
            },
            {
                //eslint-disable-next-line
                pattern: /^[A-Za-z]+$/,
                message: "Enter valid Name"
            }
        ],
    },
    last_name: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Last Name",
        rules: [
            {
                required: true,
                message: "Last Name is Required"
            },
            {
                //eslint-disable-next-line
                pattern: /^[A-Za-z]+$/,
                message: "Enter valid Name"
            }
        ],
        disabled: allFieldsDisabled.label
    },
    email: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Email",
        rules: [
            {
                required: true,
                message: "E-mail is Required"
            },
            {
                pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: "Enter valid Email"
            }
        ],
        disabled: allFieldsDisabled
    },
    phone: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Mobile Number",
        rules: [
            {
                required: true,
                message: "Mobile Number is Required"
            },
            {
                min: 10,
                message: "Enter valid mobile number "
            },
            {
                max: 10,
                message: "Enter valid mobile number "
            },
            {
                pattern: /^(?:\d*)$/,
                message: "Value should contain just number",
            },
        ],
        disabled: allFieldsDisabled
    },
    gender: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.SELECT,
        label: "Gender",
        rules: [
            {
                required: true,
                message: "Gender is Required"
            }
        ],
        options: GENDER_TYPES.map((value, label) => ({ value: String(value), label })).toList().toArray(),
        disabled: allFieldsDisabled
    },
    nature_of_association: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.SELECT,
        label: "Relationship with Company",
        rules: [
            {
                required: true,
                message: "Field is Required"
            }
        ],
        options: NATURE_OF_ASSOCIATION_TYPES.map((value, label) => ({
            label,
            value: String(value)
        })).toList().toArray(),
        disabled: allFieldsDisabled
    }
});

class AddCaseForm extends React.Component {
    state = {
        sales_manager_users: null,
        secondary_sales_manager_users: null,
        gstin: null,
        gstinLoading:  false,
        ledgerMasters: [],
        isGstinValidated: false,
        salesManagerLoader: true,
        gstinReseted: false,
        caseType: this.props.values.type,
        filtered_ASM_values: null,
        productTypeChangeMessage: "",
        loading: false
    };
    fieldGenerator = fieldGenerator.bind(this.props.form);
    getErrorOnField = getErrorOnField.bind(this.props.form);
    getValidateStatus = getValidateStatus.bind(this.props.form);
    getValidateMessage = getValidateMessage.bind(this.props.form);
    userFormFields = getUserDetails(false);
    submitForm = submitForm.bind(this.props.form, values => {
        this.props.onSubmit(
            this.props.values.merge({
                ...values, ledger_masters: values.legder_masters.map(_ => _.checked) 
            })
        );
    });

    ledgerMasterSearchByLedgerName = ledger => UnderwritingService.getSupplyChainListByLedgerName(null, ledger);
    componentDidMount() {
        if(!this.state.sales_manager_users) {
            CompanyService.getSMList().then(({data}) => {
                this.setState({
                    sales_manager_users: data,
                    isGstinValidated: false,
                    salesManagerLoader: false
                });
            }).catch(() => {
                this.setState({
                    salesManagerLoader: false
                }, () => {
                    message.error("Please check your Internet Connection");
                });
            });
        }
        this.updateGstinFromProps();
    }

    componentDidUpdate() {
        if(this.props.values.gstin && !this.state.gstin && !this.state.gstinLoading) {
            this.fetchGSTINBlob(this.props.values.gstin);
        }

        if(this.props.resetGST && this.state.gstin && !this.state.gstinReseted) {
            this.setState({
                gstin: null,
                gstinReseted: true,
                isGstinValidated: false
            });
        }
        this.updateGstinFromProps();

    }

    componentWillUnmount() {
        this.setState({
            gstin: null,
            isGstinValidated: false
        });
    }

    updateGstinFromProps() {
        if((!this.state.gstin && this.props.gstin) || (this.props.gstin && this.state.gstin && this.state.gstin.gstin !== this.props.gstin.gstin)) {
            this.setState({
                gstin: this.props.gstin
            });
        }
    }


    fetchGSTINBlob(gstin) {
        CompanyService.parseGSTIN(gstin).then(({ data }) => {
            if (!data || Object.keys(data).length === 0) {
                this.setState({
                    gstinLoading: false
                });
                return;
            }
            this.setState({
                gstin: data,
                gstinLoading: false
            });
                    
        });
    }

    updateGSTINRelatedFields = () => {
        if(this.state.gstin) {
            this.props.form.setFieldsValue({
                company_name: _startCase(_lower(this.state.gstin.tradeNam))
            });
            UnderwritingService.getSupplyChainListByGSTIN(this.state.gstin.gstin).then(({ data }) => {
                if(data && data.length > 0) {
                    this.props.form.setFieldsValue({
                        ledger_masters:data,
                        proposed_limit: Math.ceil(this.getLedgerMasterLimit(data))
                    });
                }
            });
        }
    }

    getLedgerMasterLimit(ledger_masters) {
        try {
            const sellersObj = [...ledger_masters && ledger_masters.filter(el=> el.checked).map(el=>el.seller)];
            const uniqueSellersList = [...new Map(sellersObj.map(item =>
                [item["gstin"], item])).values()];
            this.props.form.setFieldsValue({
                sellers: [...uniqueSellersList]
            });
            const ledgerMasterLimit = (ledger_masters || []).filter(_ => _.checked).reduce((limit, _) => limit + (_.ledger_analysis && +_.ledger_analysis.limit_basis_sales), 0);
            return ledgerMasterLimit || 0;
        } catch(error) {
            return 0;
        }
    }

    gstinCustomValidator = {
        validator: (rule, value, callback) => {
            if(!value){
                return callback();
            }
            if (value.length === 15 && !GSTIN_REGEX.test(value) || value.length !== 15 && value.length>0) {
                return callback("Invalid GSTIN");
            }
            
            if (value && value.length > 0 && GSTIN_REGEX.test(value) && !this.state.isGstinValidated) {
                CaseService.caseSearchList(value).then(({ data: {results} }) => {
                    this.props.showGstinDetails(results, () => {
                        if(this.state.gstin && this.state.gstin.gstin === value) {
                            if(!this.props.form.getFieldValue("company_name")){
                                this.props.form.setFieldsValue({
                                    company_name: this.state.gstin.tradeNam
                                });
                            }
                        }
                        if(value !== (this.state.gstin && this.state.gstin.gstin) || !this.state.gstin) {
                            return CompanyService.parseGSTIN(value).then(({ data }) => {
                                if (!data || Object.keys(data).length === 0) {
                                    callback("Not Found");
                                    return;
                                }
                                this.setState({
                                    gstin: data,
                                    isGstinValidated: true,

                                }, () => this.updateGSTINRelatedFields());
                                return callback();
                            });
                        }
                    }, () => {
                        callback();
                        this.props.form.resetFields();
                        this.props.form.setFieldsValue({
                            ledger_masters: []
                        });
                    });
                }).catch(() => {
                    return callback("Invalid GSTIN");
                });             
            }
            return callback();
        }
    };

    ledgerMasterCustomValidator = {
        validator: (rule, value, callback) => {
            if(value && value.length > 0) {
                const proposed_limit = this.props.form.getFieldValue("proposed_limit");
                const ledgerMasterLimit = this.getLedgerMasterLimit(value);
                if(ledgerMasterLimit !== proposed_limit) {
                    this.props.form.setFieldsValue({
                        proposed_limit: Math.ceil(ledgerMasterLimit)
                    });
                }
            }
            callback();
        }
    }

    onSelectSellers = (val) => {
        const bankValue = this.props.form.getFieldValue("bank");
        const sales_manager_user_id = this.props.form.getFieldValue("sales_manager_user_id");
        if(!bankValue) {
            this.props.form.setFieldsValue({
                bank: val[0] && val[0].bank
            });
        }
        if (!sales_manager_user_id) {
            this.props.form.setFieldsValue({
                sales_manager_user_id: val[0] && val[0].primary_relationship_manager && val[0].primary_relationship_manager.id
            });
        }

    }

    formatData = (data) => {
        if(data) {
            return data.map(_ => {
                return {
                    ..._,
                    name: `${_.name} - ${_.gst_state} (${_.gstin})`
                };
            });
        }
        return data;
    }

    showDescriptionBPT = (value)=> {
        this.setState({
            loading: true
        })
        CaseService.getCaseBusinessProductTypeDesc(this.props.caseId, value)
        .then((response) => {
            this.setState({
                productTypeChangeMessage : response.data.message,
                loading: false
            })
        })
        .catch((err) => {
            message.error(err.toString())
            this.setState({
                productTypeChangeMessage : '',
                loading: false
            })
        })
    }

    formFields = FORM_FIELDS(
        this.gstinCustomValidator,
        this.ledgerMasterCustomValidator,
        this.ledgerMasterSearchByLedgerName,
        this.formatData,
        this.showDescriptionBPT,
        this.props.stageId,
        this.props.form
    );

    onChangeSellers = (sellers) => {
        this.props.form.setFieldsValue({
            sellers
        });
    }
    onChangeCaseType = (val) => {
        this.setState({
            caseType: val
        });
        if(((this.props.values.type === 1 && val === 2) || (this.props.values.type === 2 && val === 2))){
            if(!this.props.form.getFieldValue("previous_case_via_existing_limit")){
                this.props.values.previous_case_via_existing_limit ? this.props.form.setFieldsValue({
                    previous_case_via_existing_limit: `Case Id - ${this.props.values.previous_case_via_existing_limit.case_id} | Sanction Limit - ${this.props.values.previous_case_via_existing_limit.sanction_limit}`
                }) : message.error("No Previous Case Exist");}
        }
    }

    getEntriesOnSearchASM = (str) => {
        let totalDataASM = this.state.sales_manager_users && this.state.sales_manager_users.length ? new List(this.state.sales_manager_users.map((_) => ({
            label: _.full_name,
            value: _.id
        }))) : new List([]);
        let filteredEntries = str?.length > 0 ? totalDataASM?.filter(el=>el.label.toLowerCase().includes(str.toLowerCase())) : totalDataASM;
        this.setState({filtered_ASM_values: filteredEntries});
    }
    render() {
        return (
            <Spin spinning={this.state.loading}>
                <Form
                    layout={"horizontal"}
                    hideRequireMark={true}
                    className={cx("form", "br-3")}
                    onSubmit={this.submitForm}
                >
                    <p>{this.state.productTypeChangeMessage}</p>
                    <div className={"form-row align-items-center"}>
                        <React.Fragment>
                            {this.formFields
                                .filter((_, k) => this.props.fromCaseCreate ? !["bank"].includes(k) : _ !== null)
                                .map((field, fieldName) => {
                                    if(this.props.disabledFields && this.props.disabledFields.includes(fieldName) && fieldName !=="type") {
                                        field.disabled=true;
                                    }
                                    if(fieldName === "business_product_type" && this.props.stageId == 7 && this.props.user_groups?.map(el=>el.id)?.includes(7)){
                                        field.disabled=false;
                                        field.rules = [{
                                            required: true,
                                            message: "Product Type is Required"
                                        },
                                        {
                                            pattern: /^[1-9]\d*$/,
                                            message: "Product Type is Required"
                                        }]
                                    }
                                    if(fieldName === "program_type" && this.props.stageId == 7 && this.props.user_groups?.map(el=>el.id)?.includes(7)){
                                        field.disabled=false;
                                          field.rules = [{
                                            required: true,
                                            message: "Program Type is Required"
                                        },
                                        {
                                            pattern: /^[1-9]\d*$/,
                                            message: "Program Type is Required"
                                        }]
                                    }
                                    if(this.props.disabledFields && this.props.disabledFields?.includes(fieldName) && fieldName ==="type" && !this.props.user_groups?.map(el=>el.id)?.includes(7) ) {
                                        field.disabled=true;
                                    }
                                    if(fieldName === "type"){
                                        field.onChange = this.onChangeCaseType;
                                    }
                                    if (fieldName === "sales_manager_user_id") {
                                        field.loading=this.state.salesManagerLoader;
                                        let totalDataASM = this.state.sales_manager_users && this.state.sales_manager_users.length ? new List(this.state.sales_manager_users.map((_) => ({
                                            label: _.full_name,
                                            value: _.id
                                        }))) : new List([]);
                                        field.onSearch = (str) => this.getEntriesOnSearchASM(str);
                                        field.options =  this.state.filtered_ASM_values || totalDataASM;
                                    }
                                    if(fieldName === "previous_case_via_existing_limit"){
                                        field.disabled = true;
                                    }
                                
                                    // if(this.props.mandatoryFields && this.props.mandatoryFields.includes(fieldName)) {
                                    //     field.rules = [
                                    //         ...field.rules,
                                    //         {
                                    //             required: true,
                                    //             message: "Field Required"
                                    //         }
                                    //     ];
                                    // }

                                    if(fieldName === "gstin" && this.props.gstin && this.state.gstin !==null && this.state.gstin?.can_company_created_through_pan === false && this.state.gstin?.sts !== "Active") {
                                        field.helpMessage = "Inactive GSTIN";
                                    }
                                    if(fieldName === "gstin" && !this.props.gstin && this.state.gstin !==null && this.state.gstin?.can_company_created_through_pan === false && this.state.gstin?.sts !== "Active") {
                                        message.error("Entered GSTIN is not Active");
                                        this.setState({
                                            gstin: null,
                                            company_name: null
                                        }, () => {
                                            this.props.form.setFieldsValue({
                                                gstin: null,
                                                company_name: null
                                            });
                                        });
                                    }
                                    if(fieldName === "gstin" && this.state.isGstinValidated) {
                                        field.onChange = () => {
                                            this.setState({
                                                isGstinValidated: false,
                                                gstin: null
                                            }, () => {
                                                this.props.form.setFieldsValue({
                                                    ledger_masters: []
                                                });
                                            });
                                        };
                                    }
                                    if(fieldName === "sellers") {
                                        field.onSelect = this.onSelectSellers;
                                        // field.onChange = this.onChangeSellers;
                                    }
                                    if(!(fieldName === "previous_case_via_existing_limit" && ((this.props.values.type === 1 && this.state.caseType === 2) || (this.props.values.type === 2 && this.state.caseType === 2)))) {
                                        if((fieldName === "previous_case_via_existing_limit" && ((this.props.values.type === 1 && this.state.caseType !== 2) || (this.props.values.type === 2 && this.state.caseType !== 2) || ((this.props.values.type !== 1) || (this.props.values.type !== 2))))
                                        ){
                                            return false;
                                        }
                                    }
                                    if (fieldName === "sales_manager_users") {
                                        field.loading=this.state.salesManagerLoader;
                                        field.options = this.state.sales_manager_users && this.state.sales_manager_users.length ? new List(this.state.sales_manager_users.map((_) => ({
                                            label: _.full_name,
                                            value: _.id
                                        }))) : new List([]);
                                        return (<React.Fragment key={fieldName}>{this.fieldGenerator(
                                            fieldName,
                                            field,
                                            this.getValidateStatus(fieldName),
                                            this.getValidateMessage(fieldName)
                                        )()}<div style={{ flexBasis: "100%", marginBottom: "10px" }}>
                                            <GSTINDataRenderer data={this.state.gstin} />
                                        </div></React.Fragment>);
                                    }
                                    if(fieldName === "ledger_masters") {
                                        return(
                                            <div key={fieldName} className={"w-100"}>
                                                {this.fieldGenerator(
                                                    fieldName,
                                                    field,
                                                    this.getValidateStatus(fieldName),
                                                    this.getValidateMessage(fieldName)
                                                )()}
                                            </div>
                                        );
                                    }
                                    return this.fieldGenerator(
                                        fieldName,
                                        field,
                                        this.getValidateStatus(fieldName),
                                        this.getValidateMessage(fieldName)
                                    )();
                                })
                                .toList()}
                        </React.Fragment>
                    </div>
                    {this.props.fromCaseCreate ? (
                        <React.Fragment>
                            <Divider orientation="left">{"Applicant Details"}</Divider>
                            <div className={"d-flex flex-row justify-content-space-between flex-wrap w-100"}>
                                {this.userFormFields.map((field, fieldName) => {
                                    return this.fieldGenerator(
                                        fieldName,
                                        field,
                                        this.getValidateStatus(fieldName),
                                        this.getValidateMessage(fieldName)
                                    )();
                                }).toList()}
                            </div>
                        </React.Fragment>
                    ) : null}
                </Form>
            </Spin>
        );
    }
}

AddCaseForm.propTypes = {
    form: PropTypes.object,
    onSubmit: PropTypes.func,
    values: PropTypes.object,
    applicantDetails: PropTypes.object,
    type: PropTypes.string,
    caseId: PropTypes.string,
    gstin: PropTypes.object,
    disabledFields: PropTypes.array,
    showGstinDetails: PropTypes.func,
    resetGST: PropTypes.bool,
    mandatoryFields: PropTypes.array,
    user_groups: PropTypes.array,
    fromCaseCreate: PropTypes.bool
};

export default Form.create({
    mapPropsToFields: ({ values, applicantDetails, errors }) => {
        if(values && applicantDetails) {
            return (
                new Map({
                    ...values.toJS(),
                    ...applicantDetails.toJS(),
                    type: values.type
                }).map((value, key) => {
                    return Form.createFormField({
                        value,
                        errors: errors && errors.size && errors.flatten().get(key) ? errors.flatten().get(key) : null 
                    });
                })
                    .toJS()
            );
        }
        return (
            values &&
            values
                .toSeq()
                .toMap()
                .map((value, key) => {
                    if(key === "previous_case_via_existing_limit"){
                        return Form.createFormField({
                            value: value && `Case Id - ${value.case_id} | Sanction Limit - ${value.sanction_limit}`
                        });
                    }
                    return Form.createFormField({
                        value,
                        errors: errors && errors.size && errors.flatten().get(key) ? errors.flatten().get(key) : null 
                    });
                })
                .toJS()
        );
    }
})(AddCaseForm);

